<template>
  <div class="w-full">
    <Panel header="Danh sách khẩn cấp">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
        <section class="md:col-span-12">
          <div class="rounded-lg border border-slate-200 bg-white p-4">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div class="flex min-w-0 flex-col md:col-span-4">
                <label>Từ thời gian</label>
                <InputText
                  v-model="timeFrom"
                  type="datetime-local"
                  @update:modelValue="selectedQuickRange = ''"
                />
              </div>
              <div class="flex min-w-0 flex-col md:col-span-4">
                <label>Đến thời gian</label>
                <InputText
                  v-model="timeTo"
                  type="datetime-local"
                  @update:modelValue="selectedQuickRange = ''"
                />
              </div>
              <div class="flex min-w-0 flex-col justify-end md:col-span-3">
                <Button
                  label="Làm mới danh sách"
                  icon="pi pi-refresh"
                  iconPos="left"
                  :loading="isLoading"
                  :disabled="!timeFrom || !timeTo"
                  @click="loadEmergencies"
                />
              </div>
              <div class="flex flex-wrap items-end gap-2 md:col-span-12">
                <Button
                  label="Hôm nay"
                  :outlined="selectedQuickRange !== 'today'"
                  @click="selectToday"
                />
                <Button
                  label="7 ngày gần nhất"
                  :outlined="selectedQuickRange !== 'last7Days'"
                  @click="selectLast7Days"
                />
                <Button
                  label="Tháng này"
                  :outlined="selectedQuickRange !== 'thisMonth'"
                  @click="selectThisMonth"
                />
              </div>
            </div>
          </div>
        </section>

        <section class="md:col-span-12">
          <DataTable
            v-model:filters="filters"
            :loading="isLoading"
            :value="emergencies"
            :paginator="true"
            :rows="10"
            sortField="time"
            :sortOrder="-1"
          >
            <template #header>
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div class="text-[15px] font-black text-slate-900">
                    Nội dung khẩn cấp
                  </div>
                  <div class="text-sm font-medium text-slate-500">
                    Tổng số: {{ emergencies.length }} báo cáo
                  </div>
                </div>
                <span class="relative flex w-full max-w-[420px]">
                  <i
                    class="pi pi-search pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-slate-400"
                  />
                  <InputText
                    v-model="filters['global'].value"
                    class="!pl-8 !pr-3"
                    placeholder="Tìm theo tên, số điện thoại hoặc nội dung"
                  />
                </span>
              </div>
            </template>

            <Column field="fullName" header="Họ và tên" sortable>
              <template #body="{ data }">{{ data.fullName || "-" }}</template>
            </Column>
            <Column field="phoneNumber" header="Số điện thoại" sortable>
              <template #body="{ data }">{{ data.phoneNumber || "-" }}</template>
            </Column>
            <Column field="content" header="Nội dung">
              <template #body="{ data }">
                <div class="max-w-[680px] whitespace-pre-wrap leading-5">
                  {{ data.content || "-" }}
                </div>
              </template>
            </Column>
            <Column field="time" header="Thời gian gửi" sortable>
              <template #body="{ data }">{{ formatDateTime(data.time) }}</template>
            </Column>
          </DataTable>
        </section>
      </div>
    </Panel>
  </div>
</template>

<script lang="ts">
import { nextTick, ref } from "vue";
import { FilterMatchMode } from "@/ui/services/api";
import { useToast } from "@/ui/services/toast";
import zaloRepository from "@/services/ZaloRepository";

type EmergencyItem = {
  id: number;
  content: string;
  idZalo: string;
  time: string;
  fullName: string;
  phoneNumber: string;
};

function toDateTimeLocalValue(date: Date) {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
}

function toLocalDateTimePayload(value: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return value.length === 16 ? `${value}:00` : value;
}

export default {
  setup() {
    const toast = useToast();
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);

    const timeFrom = ref(toDateTimeLocalValue(startOfDay));
    const timeTo = ref(toDateTimeLocalValue(now));
    const emergencies = ref<EmergencyItem[]>([]);
    const isLoading = ref(false);
    const selectedQuickRange = ref("today");
    let latestRequest = 0;
    const filters = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const formatDateTime = (value: string) => {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return new Intl.DateTimeFormat("vi-VN", {
        dateStyle: "short",
        timeStyle: "medium",
      }).format(date);
    };

    const loadEmergencies = async () => {
      const timeFromPayload = toLocalDateTimePayload(timeFrom.value);
      const timeToPayload = toLocalDateTimePayload(timeTo.value);
      if (!timeFromPayload || !timeToPayload || timeFromPayload > timeToPayload) {
        toast.add({
          severity: "error",
          summary: "Lỗi",
          detail: "Khoảng thời gian không hợp lệ",
          life: 2500,
        });
        return;
      }

      const requestId = ++latestRequest;
      isLoading.value = true;
      await nextTick();
      try {
        const response = await zaloRepository.getListEmergency({
          timeFrom: timeFromPayload,
          timeTo: timeToPayload,
        });
        if (requestId === latestRequest) emergencies.value = response.data || [];
      } catch (err: any) {
        if (requestId !== latestRequest) return;
        emergencies.value = [];
        toast.add({
          severity: "error",
          summary: "Lỗi",
          detail: err.response?.data || "Lỗi khi tải danh sách khẩn cấp",
          life: 2500,
        });
      } finally {
        if (requestId === latestRequest) isLoading.value = false;
      }
    };

    const applyRange = async (range: string, start: Date, end: Date) => {
      selectedQuickRange.value = range;
      timeFrom.value = toDateTimeLocalValue(start);
      timeTo.value = toDateTimeLocalValue(end);
      await nextTick();
      await loadEmergencies();
    };
    const selectToday = async () => {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      await applyRange("today", start, new Date());
    };
    const selectLast7Days = async () => {
      const start = new Date();
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      await applyRange("last7Days", start, new Date());
    };
    const selectThisMonth = async () => {
      const end = new Date();
      await applyRange(
        "thisMonth",
        new Date(end.getFullYear(), end.getMonth(), 1),
        end,
      );
    };

    loadEmergencies();
    return {
      emergencies,
      filters,
      formatDateTime,
      isLoading,
      loadEmergencies,
      selectLast7Days,
      selectedQuickRange,
      selectThisMonth,
      selectToday,
      timeFrom,
      timeTo,
    };
  },
};
</script>
